(() => {
  if (window.pharmacyFormHandlerInitialized) return;
  window.pharmacyFormHandlerInitialized = true;

  const root = window.Shopify?.routes?.root || '/';

  let saveQueue = Promise.resolve();
  let checkoutBusy = false;
  let replayingCheckout = false;

  function showError(message) {
    window.dispatchEvent(new CustomEvent('showAlert', {
      detail: {
        message,
        type: 'error'
      }
    }));
  }

  async function cartRequest(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.description || data.message || 'Cart update failed.'
      );
    }

    return data;
  }

  function updateGroup(group) {
    const yesRadio = group.querySelector(
      'input[type="radio"][value="Yes"]'
    );

    const container = group.querySelector('.extra-input');
    const input = container?.querySelector('input');

    if (!yesRadio || !container || !input) return;

    const show = yesRadio.checked;

    container.style.display = show ? 'block' : 'none';
    input.required = show;

    // Keep hidden answers so switching back to Yes restores them.
  }

  function initializeForms(scope) {
    const forms = [];

    if (
      scope instanceof Element &&
      scope.matches('.pharmacy-form')
    ) {
      forms.push(scope);
    }

    scope.querySelectorAll?.('.pharmacy-form').forEach(form => {
      forms.push(form);
    });

    forms.forEach(form => {
      form.querySelectorAll('.conditional-group').forEach(updateGroup);
    });
  }

  function collectAnswers(form) {
    const answers = {};

    form.querySelectorAll('input, select, textarea').forEach(field => {
      if (!field.name || field.disabled) return;

      const match = field.name.match(
        /^properties\[[^\]]+\]\[(.+)\]$/
      );

      if (!match) return;

      const propertyName = match[1];

      // Explicitly overwrite a previously saved confirmation.
      if (field.type === 'checkbox') {
        answers[propertyName] = field.checked ? field.value : 'No';
        return;
      }

      if (field.type === 'radio' && !field.checked) return;

      answers[propertyName] = field.value;
    });

    // Read validity without triggering validation messages or invalid events.
    const fields = [
    ...form.querySelectorAll('input, select, textarea')
    ];

    const isValid = fields.every(field =>
      !field.willValidate || field.validity.valid
    );

    answers['_questionnaire-completed'] = isValid ? 'true' : 'false';



    return answers;
  }

  async function saveAnswers(form, answers) {
    if (!form.isConnected) {
      throw new Error('Cart changed before the answers could be saved.');
    }

    form.dataset.saveState = 'saving';

    try {
      const cart = await cartRequest('cart.js');

      const index = cart.items.findIndex(
        item => item.key === form.dataset.lineKey
      );

      if (index === -1) {
        throw new Error('Cart item changed. Please refresh the cart.');
      }

      const item = cart.items[index];

      const unchanged = Object.keys(answers).every(
        name => (item.properties?.[name] ?? '') === answers[name]
      );

      if (unchanged) {
        form.dataset.saveState = 'saved';
        return;
      }

      const updatedCart = await cartRequest('cart/change.js', {
        id: item.key,
        quantity: item.quantity,
        properties: {
          ...(item.properties || {}),
          ...answers
        }
      });

      // Do not guess line positions if items merged or disappeared.
      if (
        updatedCart.items.length !== cart.items.length ||
        updatedCart.items.some(
          (updatedItem, position) =>
            updatedItem.variant_id !== cart.items[position].variant_id
        )
      ) {
        throw new Error('Cart lines changed. Please refresh the cart.');
      }

      // Property updates can change line keys.
      document.querySelectorAll('.pharmacy-form').forEach(otherForm => {
        const previousIndex = cart.items.findIndex(
          oldItem => oldItem.key === otherForm.dataset.lineKey
        );

        if (previousIndex !== -1) {
          otherForm.dataset.lineKey =
            updatedCart.items[previousIndex].key;
        }
      });

      form.dataset.saveState = 'saved';
    } catch (error) {
      form.dataset.saveState = 'error';
      throw error;
    }
  }

  function enqueueSave(form) {
    // Capture the answers at the time of this change.
    const answers = collectAnswers(form);

    const job = saveQueue.then(() => saveAnswers(form, answers));

    // A failed request must not block future retries.
    saveQueue = job.catch(() => {});

    return job;
  }

  document.addEventListener('change', event => {
    if (!(event.target instanceof Element)) return;

    const field = event.target;
    const form = field.closest('.pharmacy-form');

    if (!form) return;

    const group = field.closest('.conditional-group');

    if (group && field.matches('input[type="radio"]')) {
      updateGroup(group);
    }

    if (!field.name || checkoutBusy) return;

    enqueueSave(form).catch(() => {
      showError(
        'Your answers could not be saved. Please try again before leaving the cart.'
      );
    });
  });

  // Wait for saves before forwarding the checkout click.
  document.addEventListener('click', async event => {
    if (replayingCheckout) return;
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(
      "#main-cart button[data-type='submit']"
    );

    if (!button) return;

    const cartElement = button.closest('#main-cart');
    const forms = [...cartElement.querySelectorAll('.pharmacy-form')];

    if (!forms.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (checkoutBusy) return;
    checkoutBusy = true;

    try {
      // Save current values, including a field still being edited.
      for (const form of forms) {
        await enqueueSave(form);
      }

      if (!button.isConnected) {
        throw new Error('Cart changed. Please try checkout again.');
      }

      replayingCheckout = true;

      try {
        // Your existing handler performs validation and checkout.
        button.click();
      } finally {
        replayingCheckout = false;
      }
    } catch (error) {
      // showError(
      //   'Could not save your answers. Please refresh the cart and try again.'
      // );
    } finally {
      checkoutBusy = false;
    }
  }, true);

  function start() {
    initializeForms(document);

    // Initialize forms inserted by cart or theme-editor updates.
    const observer = new MutationObserver(records => {
      records.forEach(record => {
        record.addedNodes.forEach(node => {
          if (node instanceof Element) {
            initializeForms(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  document.addEventListener('input', event => {
    const input = event.target;

    if (
        !(input instanceof HTMLInputElement) ||
        !input.matches('.phone-validate')
    ) return;

    input.value = input.value.replace(/\D/g, '');
    });


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
